"use client";

import { useEffect, useLayoutEffect, useState } from "react";
import { PostHogProvider } from "@posthog/react";
import { POSTHOG_KEY, POSTHOG_HOST, initPixels, registerSuperProps } from "@/lib/analytics";
import { captureUtms } from "@/lib/utm";
import { capturePrefill, PII_PARAMS } from "@/lib/lead-prefill";
import type { ABBootstrap } from "@/lib/ab-bootstrap";
import { ABBootstrapContext } from "@/hooks/useABTest";

// Retire les paramètres personnels des URL enregistrées par PostHog.
const stripPii = (url: unknown) => {
  if (typeof url !== "string") return url;
  try {
    const u = new URL(url, "https://x.invalid");
    for (const k of PII_PARAMS) u.searchParams.delete(k);
    return url.startsWith("http") ? u.toString() : u.pathname + u.search + u.hash;
  } catch {
    return url;
  }
};

const options = {
  api_host: POSTHOG_HOST,
  // Capture les pageviews aussi sur les changements d'historique (URL par étape)
  capture_pageview: "history_change",
  // Dernier filet : un lien personnalisé ne doit pas laisser d'email dans
  // $current_url, même si le nettoyage de l'URL a échoué.
  sanitize_properties: (props: Record<string, any>) => ({
    ...props,
    $current_url: stripPii(props.$current_url),
    $initial_current_url: stripPii(props.$initial_current_url),
    $referrer: stripPii(props.$referrer),
  }),
} as const;

// Effet de layout côté client, effet simple au SSR (useLayoutEffect y avertit).
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function Providers({
  children,
  bootstrap,
}: {
  children: React.ReactNode;
  bootstrap: ABBootstrap | null;
}) {
  // Figées au premier rendu : PostHogProvider n'initialise qu'une fois.
  // Même distinct_id que celui utilisé par le serveur → mêmes variantes.
  const [phOptions] = useState(() => (bootstrap ? { ...options, bootstrap } : options));

  // Effet de LAYOUT, volontairement : les données personnelles doivent quitter
  // l'URL avant que le moindre pixel ne la lise. React exécute TOUS les effets
  // de layout avant TOUS les effets passifs — or PostHog, Meta, Google et le
  // relais OpenAI partent tous depuis des effets passifs. Le faire en phase de
  // rendu ne suffit pas : le routeur de Next restaure ensuite l'URL.
  useIsomorphicLayoutEffect(() => {
    capturePrefill();
  }, []);

  useEffect(() => {
    initPixels();
    // UTM : capturés sur chaque page, joints à tous les events (super-properties)
    registerSuperProps(captureUtms());
  }, []);

  return (
    <PostHogProvider apiKey={POSTHOG_KEY} options={phOptions}>
      <ABBootstrapContext.Provider value={bootstrap?.featureFlags ?? null}>
        {children}
      </ABBootstrapContext.Provider>
    </PostHogProvider>
  );
}
